import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Package, ArrowLeft, Share2 } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import ShareButtons from '../components/ShareButtons';
import useOGMeta from '../hooks/useOGMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FlyerPage() {
  const { flyerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/flyers/${flyerId}`)
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [flyerId]);

  const flyer = data?.flyer;
  const products = data?.products;
  const shop = data?.shop;
  const draftCount = data?.draft_count || 0;
  const flyerUrl = `${window.location.origin}/volantino/${flyerId}`;
  const shareProduct = { title: flyer?.title || 'Offerte', price: 0 };

  // Determine shop context: from ?shop= query, otherwise infer from flyer.shop data
  const queryShopSlug = searchParams.get('shop') || '';
  const shopSlug = queryShopSlug || data?.shop?.slug || '';
  const backTarget = shopSlug ? `/negozio/${shopSlug}` : -1;

  const ogImage = products?.[0]?.images?.[0]?.external_url || '';
  const priceList = products?.filter(p => p.price).map(p => p.price) || [];
  const priceRange = priceList.length ? `da ${Math.min(...priceList).toFixed(0)}€ a ${Math.max(...priceList).toFixed(0)}€` : '';

  useOGMeta({
    title: data ? `${shop?.name || 'Volantino'} - ${flyer?.title || 'Offerte'}` : 'Volantino',
    description: data ? `${products?.length || 0} prodotti ${priceRange} - Scopri le offerte!` : '',
    image: ogImage,
    url: flyerUrl
  });

  if (loading) {
    return (
      <div className="pb-safe px-4 pt-4 space-y-4">
        <div className="h-12 skeleton-shimmer rounded-xl" />
        <div className="grid grid-cols-2 gap-3">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-2xl border border-[#E8E3D9] overflow-hidden">
              <div className="aspect-[4/5] skeleton-shimmer" />
              <div className="p-3 space-y-2">
                <div className="h-4 skeleton-shimmer rounded" />
                <div className="h-5 skeleton-shimmer rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-8 text-center text-[#877E78]">Volantino non trovato</div>;

  return (
    <div data-testid="flyer-page" className="pb-safe">
      {/* Header */}
      <div className="bg-white border-b border-[#E8E3D9] px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            data-testid="flyer-back-btn"
            onClick={() => typeof backTarget === 'string' ? navigate(backTarget) : navigate(-1)}
            className="p-1.5 rounded-full hover:bg-[#F0EBE1]"
            aria-label="Torna indietro"
          >
            <ArrowLeft size={20} className="text-[#2A2624]" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
              {shop?.name || 'Volantino'}
            </h1>
            <div className="flex items-center gap-1.5 text-xs text-[#877E78]">
              <Calendar size={12} />
              <span>{flyer?.title || flyer?.date}</span>
              <span>-</span>
              <Package size={12} />
              <span>{products?.length || 0} prodotti</span>
            </div>
          </div>
        </div>
      </div>

      {/* Share bar */}
      <div className="px-4 py-3 bg-[#F0EBE1]/50">
        <p className="text-xs font-bold text-[#2A2624] uppercase tracking-wider mb-2">Condividi Volantino</p>
        <ShareButtons product={shareProduct} productUrl={flyerUrl} shop={shop} kind="flyer" resourceId={flyerId} />
      </div>

      {/* Products */}
      <div className="px-4 py-4">
        {draftCount > 0 && (
          <div data-testid="flyer-draft-warning" className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
            <strong>{draftCount} prodott{draftCount === 1 ? 'o' : 'i'} in bozza</strong> non {draftCount === 1 ? 'è' : 'sono'} visibile in questo volantino. Vai su Admin → Catalogo, pubblica le bozze e poi aggiungile di nuovo al volantino.
          </div>
        )}
        {products?.length === 0 ? (
          <div className="text-center py-12">
            <Package size={40} className="mx-auto text-[#877E78] mb-3" />
            <p className="text-[#877E78]">Nessun prodotto in questo volantino</p>
            {draftCount > 0 && (
              <p className="text-xs text-[#877E78] mt-2">Hai {draftCount} bozz{draftCount === 1 ? 'a' : 'e'} da pubblicare prima.</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {products?.map(p => <ProductCard key={p.id} product={p} shopSlug={shopSlug} />)}
          </div>
        )}
      </div>
    </div>
  );
}
